import { differenceInMinutes, isBefore } from 'date-fns';
import { z } from 'zod';

import { getPhoenixDate } from '@libs/shared-ui';

const MAX_PREFIX_LENGTH = 5;
const MAX_NAME_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 250;
const MAX_GROUPS_LENGTH = 50;

const ALPHANUMERIC_UC_REGEX = /^[A-Z0-9]*$/;
const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]+$/;

export const bulkPromoSchema = z
    .object({
        discountCodeName: z
            .string()
            .max(MAX_NAME_LENGTH, `Up to ${MAX_NAME_LENGTH} symbols are allowed`)
            .optional()
            .or(z.literal('')),

        discountCodeDescription: z
            .string()
            .max(MAX_DESCRIPTION_LENGTH, `Up to ${MAX_DESCRIPTION_LENGTH} symbols are allowed`)
            .optional()
            .or(z.literal('')),

        quantity: z.preprocess(
            val => {
                if (val === '') return undefined;
                if (typeof val === 'string' || typeof val === 'number') {
                    const num = Number(val);
                    return Number.isNaN(num) ? val : num;
                }
                return val;
            },
            z
                .number({
                    error: issue =>
                        issue.input === undefined ? 'Quantity is required' : 'Quantity must be a valid number',
                })
                .int({ message: 'Quantity must be a whole number' })
                .min(1, { message: 'Quantity must be greater than 0' })
                .max(10_000_000, { message: 'Up to 10 000 000 can be generated at once' })
        ),

        prefix: z
            .string()
            .max(MAX_PREFIX_LENGTH, `Up to ${MAX_PREFIX_LENGTH} symbols are allowed`)
            .regex(ALPHANUMERIC_UC_REGEX, 'Only uppercase letters and digits are allowed')
            .optional()
            .or(z.literal('')),

        groups: z
            .string()
            .max(MAX_GROUPS_LENGTH, `Up to ${MAX_GROUPS_LENGTH} symbols are allowed`)
            .regex(ALPHANUMERIC_REGEX, 'Only letters and digits are allowed')
            .optional()
            .or(z.literal('')),

        maxApplications: z.preprocess(
            val => (val === '' ? undefined : val),
            z.coerce
                .number({ error: () => 'Quantity must be a number' })
                .int({ message: 'Quantity must be an integer' })
                .min(1, { message: 'Quantity must be greater than 0' })
                .optional()
        ),

        maxApplicationsPerCustomer: z.preprocess(
            val => (val === '' ? undefined : val),
            z.coerce
                .number({ error: () => 'Quantity must be a number' })
                .int({ message: 'Quantity must be an integer' })
                .min(1, { message: 'Quantity must be greater than 0' })
                .optional()
        ),

        cartDiscount: z
            .string({ error: issue => (issue.input === undefined ? 'Cart Discount is required' : undefined) })
            .min(1, { message: 'Cart Discount cannot be empty' }),

        validFrom: z.preprocess(
            val => {
                if (val === '' || val == null) return undefined;
                if (typeof val === 'string') {
                    const date = new Date(val);
                    return isNaN(date.getTime()) ? val : date;
                }
                return val;
            },
            z
                .date({
                    error: issue =>
                        issue.input === undefined ? 'Valid From is required' : 'Valid From must be a valid date',
                })
                .refine(date => !isBefore(date, getPhoenixDate()), {
                    message: 'Valid From date cannot be in the past',
                })
        ),
        validTo: z.preprocess(
            val => {
                if (val === '' || val == null) return undefined;
                if (typeof val === 'string') {
                    const date = new Date(val);
                    return isNaN(date.getTime()) ? val : date;
                }
                return val;
            },
            z.date({
                error: issue => (issue.input === undefined ? 'Valid To is required' : 'Valid To must be a valid date'),
            })
        ),
    })
    .refine(
        data => {
            if (!data.validFrom || !data.validTo) return true;
            return differenceInMinutes(data.validTo, data.validFrom) >= 30;
        },
        {
            path: ['validTo'],
            message: 'Must be at least 30 minutes after Valid From Date',
        }
    );
